import { gte, intersection, isEmpty, lte } from 'lodash';
import date_helper from 'utils/date-helper';

export const isFormaStoreApplicable = (
  all_applicable_wallets: Array<{
    company_wallet_configuration: { is_twic_store_enabled?: boolean };
  }>
): boolean =>
  !!all_applicable_wallets.some(
    ({ company_wallet_configuration }) =>
      company_wallet_configuration.is_twic_store_enabled
  );

export const daysUntilReset = (employee_wallet: {
  next_credit_date: string;
}): number =>
  date_helper(employee_wallet.next_credit_date).diff(date_helper(), 'days') + 1;

export const fundsAreExpiring = (employee_wallet: {
  next_credit_date: string;
  visible_to_employee: boolean;
  next_renew_amount_loss?: number;
}): boolean => {
  const resets_in = daysUntilReset(employee_wallet);

  return (
    gte(resets_in, 1) &&
    lte(resets_in, 10) &&
    employee_wallet.visible_to_employee &&
    (employee_wallet?.next_renew_amount_loss as number) > 0
  );
};

export const isVisibleToEmployee = (employee_wallet: {
  visible_to_employee: boolean;
}): boolean => employee_wallet.visible_to_employee;

export const getExpiringFunds = (employee_wallet: {
  next_credit_date: string;
  visible_to_employee: boolean;
  next_renew_amount_loss?: number;
}): number =>
  fundsAreExpiring(employee_wallet)
    ? getExpiringFundsAmount(employee_wallet)
    : 0;

export const getExpiringFundsTotal = (
  employee_wallets: Array<{
    next_credit_date: string;
    visible_to_employee: boolean;
    next_renew_amount_loss?: number;
  }>
): number =>
  employee_wallets.reduce((prev, ew) => prev + getExpiringFunds(ew), 0);

export const getExpiringFundsTotalAndDays = (
  employee_wallets: Array<{
    next_credit_date: string;
    visible_to_employee: boolean;
    next_renew_amount_loss?: number;
  }>
): { expiring_funds_days_left: number; expiring_funds_total: number } => ({
  expiring_funds_days_left: getLeastDaysBeforeExpiry(employee_wallets),
  expiring_funds_total: getExpiringFundsTotal(employee_wallets),
});

export const getFundsTotal = (
  employee_wallets: Array<{
    amount?: number;
  }>
): number => employee_wallets.reduce((prev, ew) => prev + getFunds(ew), 0);

export const getLeastDaysBeforeExpiry = <
  T extends {
    next_credit_date: string;
    visible_to_employee: boolean;
    next_renew_amount_loss?: number;
  }
>(
  employee_wallets: Array<T>,
  number_of_days_max = 100
): number => {
  const days_until_resets = employee_wallets.map(daysUntilReset);
  days_until_resets.push(number_of_days_max);
  return Math.min(...days_until_resets);
};

export const getRelevantWalletsForProduct = <
  T extends {
    company_wallet_configuration: {
      categories: Array<{
        id: string;
      }>;
    };
  }
>(
  employee_wallets: Array<T>,
  product: { categories: Array<string> }
) =>
  employee_wallets.filter(
    (wallet) =>
      !isEmpty(
        intersection(
          wallet.company_wallet_configuration.categories.map(({ id }) => id),
          product.categories
        )
      )
  );

export const getWalletsExpiring = <
  T extends {
    next_credit_date: string;
    visible_to_employee: boolean;
    next_renew_amount_loss?: number;
  }
>(
  employee_wallets: Array<T>
) => employee_wallets.filter(fundsAreExpiring);

/**
 * The amount of funds in this wallet
 * @param employee_wallet
 */
export const getFunds = (employee_wallet: { amount?: number }): number =>
  employee_wallet.amount as number;

/**
 * The amount of expiring funds in this wallet
 * @param employee_wallet
 */
export const getExpiringFundsAmount = (employee_wallet: {
  next_renew_amount_loss?: number;
}): number => employee_wallet.next_renew_amount_loss as number;

export const storeIsEnabled = (employee_wallet: {
  company_wallet_configuration: { is_twic_store_enabled?: boolean };
}): boolean =>
  Boolean(employee_wallet.company_wallet_configuration.is_twic_store_enabled);

export const getCategoryAlias = <
  Product extends { categories: Array<string> },
  Wallet extends {
    company_wallet_configuration: {
      categories: Array<{ id: string; alias: string }>;
    };
  }
>({
  product,
  wallets = [],
}: {
  product?: Product;
  wallets?: Array<Wallet>;
}) =>
  isEmpty(product?.categories) || isEmpty(wallets)
    ? ''
    : wallets.reduce(
        (
          intial_alias: string,
          {
            company_wallet_configuration,
          }: {
            company_wallet_configuration: {
              categories: Array<{ id: string; alias: string }>;
            };
          }
        ) => {
          intial_alias = company_wallet_configuration?.categories.reduce(
            (new_alias, { id, alias }: { id: string; alias: string }) => {
              if (id === product!.categories[0] && alias) {
                new_alias = alias;
              }
              return new_alias;
            },
            ''
          );
          return intial_alias;
        },
        ''
      );

/**
 * Produces a grammatically correct timespan for the employee wallet renewal phrase
 * If the wallets have different timespans, an empty string is returned.
 * @param employee_wallets wallets being summarized
 * @returns string Date of the last stipend remittance
 */
export const renewalFrequency = (
  employee_wallets: Array<{
    company_wallet_configuration: { renew_frequency: string };
  }>
): string => {
  if (isEmpty(employee_wallets)) {
    return '';
  }

  const { renew_frequency } = employee_wallets[0].company_wallet_configuration;
  if (
    !employee_wallets.every(
      ({ company_wallet_configuration: { renew_frequency: frequency } }) =>
        frequency === renew_frequency
    )
  ) {
    return '';
  }

  switch (renew_frequency) {
    case 'annually':
      return 'this year';
    case 'semiannually':
    case 'semi-annually':
      return 'in this half of the year';
    case 'quarterly':
      return 'this quarter';
    case 'monthly':
      return 'this month';
    case 'weekly':
      return 'this week';
    case 'daily':
      return 'today';
    default:
      return '';
  }
};
